// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { aiSummaryEvidenceHealthKey, aiSummaryGraphEvidenceText } from "./summary-markup.js";

// All of these used to be inline functions in app.js that read flightData,
// healthSummary, healthMetricLabels, and a handful of analysis helpers from
// module-level state. They're now pure: the caller passes a `context` bag
// with everything they need. app.js keeps thin wrappers that build that bag
// from its globals on each call.

export function aiSummaryAvailableSeries(keys = [], { availableSeriesKeys }) {
  const available = new Set(availableSeriesKeys());
  return [...new Set(keys.filter(Boolean))].filter((key) => available.has(key));
}

export function aiSummaryThermalPeerKeys(prefix, {
  flightData,
  cylinderKeys,
  targetCylinder = null,
  limit = 6,
}) {
  const keys = cylinderKeys(prefix).filter((key) => flightData.some((point) => Number.isFinite(point[key])));
  const targetKey = Number.isFinite(targetCylinder) ? `${prefix}${targetCylinder}` : null;
  const ordered = targetKey ? [targetKey, ...keys.filter((key) => key !== targetKey)] : keys;
  return ordered.slice(0, limit);
}

export function aiSummaryTargetCylinder(metric, windows = []) {
  const candidates = [
    metric?.sourceCylinder,
    metric?.details?.dominant?.cylinder,
    metric?.details?.peak?.high?.cylinder,
    metric?.details?.peak?.low?.cylinder,
    metric?.details?.top?.cylinder,
    metric?.details?.hottest?.cylinder,
    windows.find((window) => Number.isFinite(window?.sourceCylinder))?.sourceCylinder,
  ];
  return candidates.find(Number.isFinite) ?? null;
}

export function aiSummarySeriesForHealthKey(healthKey, metric, windows = [], context) {
  const { flightData, cylinderKeys, availableSeriesKeys } = context;
  const targetCylinder = aiSummaryTargetCylinder(metric, windows);
  const targetChannel = windows.find((window) => window?.channelKey)?.channelKey ?? metric?.channelKey ?? null;
  const power = ["rpm", "map", "fuelFlow"];
  const speed = ["ias", "groundSpeed"].find((key) => flightData.some((point) => Number.isFinite(point[key])));
  const peers = (prefix, peerLimit) => aiSummaryThermalPeerKeys(prefix, { flightData, cylinderKeys, targetCylinder, limit: peerLimit });
  const map = {
    egt: [...peers("egt", 6), "rpm", "fuelFlow"],
    valve: [targetChannel, ...peers("egt", 4), ...power],
    combustion: [`egt${targetCylinder}`, `cht${targetCylinder}`, ...power],
    cht: [...peers("cht", 6), "rpm", "map"],
    cooling: [`cht${targetCylinder}`, ...peers("cht", 4), ...power, "percentPower", speed],
    coolingRate: [`cht${targetCylinder}`, ...power, "percentPower", speed],
    divergence: [...peers("egt", 4), ...peers("cht", 3), ...power],
    descentEgtDropout: [targetChannel, ...peers("egt", 4), "fuelFlow", "map", "rpm", "altitude"],
    inductionLeak: [targetChannel, ...peers("egt", 4), ...power],
    mag: ["rpm", ...peers("egt", 4)],
    carbIce: ["carbTemp", "oat", ...power],
    turbo: ["tit", "tit1", "tit2", "map", "upperDeckPressure", "fuelFlow", "rpm", "altitude"],
    oilSystem: ["oilPressure", "oilPressure1", "oilPressure2", "oilTemp", "oilTemp1", "oilTemp2", "rpm"],
    charging: ["buss", "amps", "rpm"],
    electrical: ["buss", "amps", "rpm"],
    sensors: [targetChannel, "rpm", "map", "fuelFlow", ...peers("egt", 3), ...peers("cht", 3)],
    sensorCoherence: ["rpm", "map", "fuelFlow", ...peers("egt", 3), ...peers("cht", 3)],
    engineDelta: ["rpm", "map", "fuelFlow", "cht", "oilPressure", "oilTemp"],
    thermalContext: [...peers("cht", 4), "rpm", "map", "fuelFlow", "percentPower", speed, "altitude"],
    cylinderHarmony: [...peers("egt", 4), ...peers("cht", 4), "fuelFlow", "rpm", "map"],
    cylinderStartupSymmetry: [...peers("egt", 4), ...peers("cht", 4), "fuelFlow", "rpm", "buss"],
    powerChangeContext: ["rpm", "map", "fuelFlow", "percentPower", ...peers("egt", 3), ...peers("cht", 3)],
    patternWorkContext: ["fuelFlow", ...peers("egt", 4), ...peers("cht", 4), "buss", "oat", "carbTemp"],
    // Both leaning metrics feed the one Mixture card, so they share a series list.
    ropLeaningContext: ["fuelFlow", ...peers("egt", 4), ...peers("cht", 3), "rpm", "map", "percentPower"],
    lopLeaningContext: ["fuelFlow", ...peers("egt", 4), ...peers("cht", 3), "rpm", "map", "percentPower"],
    fuelFlowResponse: ["fuelFlow", "rpm", "map", "percentPower", ...peers("egt", 3)],
    fuelFlowWorkload: ["fuelFlow", "rpm", "map", ...peers("egt", 3), ...peers("cht", 3)],
  };
  return aiSummaryAvailableSeries(map[healthKey] ?? ["rpm", "map", "fuelFlow", "egt", "cht"], { availableSeriesKeys }).slice(0, 7);
}

export function aiSummaryWindowedData(data, windows = []) {
  const finiteWindows = windows.filter((window) => Number.isFinite(window?.startTime));
  if (!finiteWindows.length) return data;
  const minStart = Math.min(...finiteWindows.map((window) => window.startTime));
  const maxEnd = Math.max(...finiteWindows.map((window) => Number.isFinite(window.endTime) ? window.endTime : window.startTime));
  const pad = Math.max(45, (maxEnd - minStart) * 0.4);
  const start = Math.max(data[0]?.elapsedSeconds ?? minStart, minStart - pad);
  const end = Math.min(data.at(-1)?.elapsedSeconds ?? maxEnd, maxEnd + pad);
  const windowed = data.filter((point) => point.elapsedSeconds >= start && point.elapsedSeconds <= end);
  return windowed.length >= 3 ? windowed : data;
}

export function aiSummaryGraphSpec(id, sectionLabel, evidenceKeys, evidenceText, options = {}, context) {
  const {
    flightData,
    healthSummary,
    calculateHealthSummary,
    detectionWindowsForHealthKey,
    compactHealthNote,
    healthMetricLabels,
    healthSummaryOptions,
  } = context;
  if (!flightData.length) return null;
  if (options.context) {
    const series = aiSummaryAvailableSeries(
      ["rpm", "map", "fuelFlow", "altitude", "ias", "groundSpeed"],
      { availableSeriesKeys: context.availableSeriesKeys },
    );
    if (!series.length) return null;
    return {
      id,
      title: "Flight context trace",
      subtitle: "Power and phase context behind the prose",
      healthKey: null,
      series,
      windows: [],
      data: flightData,
    };
  }
  const healthKey = aiSummaryEvidenceHealthKey(evidenceKeys, evidenceText);
  const summary = healthSummary ?? calculateHealthSummary(flightData, healthSummaryOptions());
  const metric = healthKey ? summary?.[healthKey] : null;
  if (!healthKey || !metric) return null;
  const windows = detectionWindowsForHealthKey(healthKey, metric).slice(0, 3);
  const series = aiSummarySeriesForHealthKey(healthKey, metric, windows, context);
  if (!series.length) return null;
  const title = `${sectionLabel}: ${healthMetricLabels[healthKey] ?? "supporting evidence"}`;
  const subtitle = windows[0]?.label ?? compactHealthNote(healthKey, metric);
  return {
    id,
    title,
    subtitle,
    healthKey,
    series,
    windows,
    data: aiSummaryWindowedData(flightData, windows),
  };
}

function aiSummaryUniqueGraphSpec(usedHealthKeys, ...args) {
  const spec = aiSummaryGraphSpec(...args);
  if (!spec?.healthKey) return spec;
  if (usedHealthKeys.has(spec.healthKey)) return null;
  usedHealthKeys.add(spec.healthKey);
  return spec;
}

export function aiSummaryGraphSpecs(summary, context) {
  if (!summary || !context?.flightData?.length) return {};
  const standouts = Array.isArray(summary.whatStandsOut) ? summary.whatStandsOut : [];
  const evidence = Array.isArray(summary.supportingEvidence) ? summary.supportingEvidence : [];
  const operatingContext = Array.isArray(summary.operatingContext) ? summary.operatingContext : [];
  const caveats = Array.isArray(summary.caveats) ? summary.caveats : [];
  const usedHealthKeys = new Set();
  return {
    quickRead: aiSummaryGraphSpec("quickRead", "Quick Read", [], summary.overview, { context: true }, context),
    standouts: aiSummaryUniqueGraphSpec(usedHealthKeys, "standouts", "What Stands Out", standouts.flatMap((item) => item?.evidenceKeys ?? []), aiSummaryGraphEvidenceText(standouts), {}, context),
    evidence: aiSummaryUniqueGraphSpec(usedHealthKeys, "evidence", "Evidence", evidence.flatMap((item) => item?.evidenceKeys ?? []), aiSummaryGraphEvidenceText(evidence), {}, context),
    context: aiSummaryGraphSpec("context", "Operating Context", operatingContext.flatMap((item) => item?.evidenceKeys ?? []), aiSummaryGraphEvidenceText(operatingContext), { context: true }, context),
    caveats: aiSummaryUniqueGraphSpec(usedHealthKeys, "caveats", "Caveats", caveats.flatMap((item) => item?.evidenceKeys ?? []), aiSummaryGraphEvidenceText(caveats), {}, context),
  };
}
